
import { useState, FormEvent, ChangeEvent } from 'react';
import { Navbar } from '../../components/feature/Navbar';
import { Footer } from '../../components/feature/Footer';
import { useSEO } from '../../utils/seo';

export default function ContactPage() {
  useSEO({
    title: 'Contact S&L Contractors | Free Estimate Mobile AL | Home Improvement Quote',
    description:
      'Contact S&L Contractors for a free home improvement estimate in Mobile & Baldwin Counties AL. Call 251-348-8514 or email SL.Contractors.AL@gmail.com. Licensed, insured, 20+ years experience.',
    keywords:
      'contact contractor Mobile AL, free estimate Baldwin County, home improvement quote Alabama, remodeling contractor phone, S&L Contractors contact',
    canonical: '/contact',
    ogType: 'website',
    schemas: [
      {
        '@context': 'https://schema.org',
        '@type': 'ContactPage',
        name: 'Contact S&L Contractors of Alabama',
        url: 'https://www.slcontractorsofal.com/contact',
        mainEntity: {
          '@type': 'GeneralContractor',
          '@id': 'https://www.slcontractorsofal.com/#organization',
          name: 'S&L Contractors of Alabama, Inc.',
          telephone: ['+1-251-348-8514', '+1-251-454-1737'],
          email: 'SL.Contractors.AL@gmail.com',
          address: {
            '@type': 'PostalAddress',
            addressLocality: 'Mobile',
            addressRegion: 'AL',
            postalCode: '36609',
            addressCountry: 'US',
          },
          geo: {
            '@type': 'GeoCoordinates',
            latitude: 30.6954,
            longitude: -88.0399,
          },
          openingHoursSpecification: [
            {
              '@type': 'OpeningHoursSpecification',
              dayOfWeek: [
                'Monday',
                'Tuesday',
                'Wednesday',
                'Thursday',
                'Friday',
                'Saturday',
              ],
              opens: '08:00',
              closes: '20:00',
            },
          ],
          sameAs: ['https://www.facebook.com/profile.php?id=61551840254912'],
        },
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          {
            '@type': 'ListItem',
            position: 1,
            name: 'Home',
            item: 'https://www.slcontractorsofal.com/',
          },
          {
            '@type': 'ListItem',
            position: 2,
            name: 'Contact',
            item: 'https://www.slcontractorsofal.com/contact',
          },
        ],
      },
    ],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
    company_url: '', // honeypot — real visitors never see this field
  });

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage('');

    // Enquiries go to S&L's own CRM account, not to the site-builder's form
    // service. They land in the client portal and are emailed straight to
    // SL.Contractors.AL@gmail.com. `source` is what scopes them to this
    // business — it must stay 'slcontractors-site'.
    try {
      const response = await fetch('https://crm.nextgearwebservices.com/api/public/lead-intake', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          service: formData.service,
          message: formData.message,
          company_url: formData.company_url,
          source: 'slcontractors-site',
        }),
      });

      if (!response.ok) {
        throw new Error(String(response.status));
      }

      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', service: '', message: '', company_url: '' });
    } catch (error) {
      // A failure here must be VISIBLE. Logging it to the console and showing
      // the visitor nothing is how an enquiry gets lost with everyone assuming
      // it arrived — the phone number below is the fallback that always works.
      console.error('Form submission error:', error);
      const status = error instanceof Error ? error.message : '';
      setErrorMessage(
        status === '429'
          ? "You've sent a few messages already. Please give it a few minutes, or call us at 251-348-8514."
          : "Sorry — we couldn't send that. Please call 251-348-8514 or email SL.Contractors.AL@gmail.com and we'll get right back to you."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f5f0e6]">
      <Navbar />

      {/* Breadcrumb */}
      <nav className="pt-24 pb-2 px-4 max-w-7xl mx-auto" aria-label="Breadcrumb">
        <ol className="flex items-center text-sm text-gray-500 space-x-2">
          <li>
            <a href="/" className="hover:text-sky-600 cursor-pointer">
              Home
            </a>
          </li>
          <li>
            <span>/</span>
          </li>
          <li className="text-gray-900 font-medium">Contact</li>
        </ol>
      </nav>

      {/* Hero Section */}
      <header className="pt-4 pb-16 px-4 bg-gradient-to-br from-[#ebe5d9] to-[#f5f0e6]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Get Your Free Home Improvement Quote
          </h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Ready to start your <strong>home improvement</strong> project? Contact us today for a free,
            no-obligation estimate. We serve <strong>Mobile &amp; Baldwin Counties</strong>, Alabama.
          </p>
        </div>
      </header>

      {/* Contact Section */}
      <main className="py-20 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Quote Request Form */}
          <section className="mb-20" aria-labelledby="quote-form-heading">
            <h2
              id="quote-form-heading"
              className="text-2xl font-bold text-gray-900 mb-2 text-center"
            >
              Send Us a Message
            </h2>
            <p className="text-gray-600 text-center mb-8">
              Tell us about your project and we&apos;ll get back to you with a free estimate.
            </p>

            {isSubmitted ? (
              <div
                className="bg-white rounded-lg shadow-sm p-8 text-center"
                role="status"
                aria-live="polite"
              >
                <div className="w-14 h-14 flex items-center justify-center bg-sky-50 rounded-full mx-auto mb-4">
                  <i className="ri-check-line text-3xl text-sky-600"></i>
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Thank you!</h3>
                <p className="text-gray-600">
                  Your message is on its way to us. We&apos;ll be in touch shortly — usually the
                  same business day. Need us sooner? Call{' '}
                  <a href="tel:251-348-8514" className="text-sky-600 hover:text-sky-700">
                    251-348-8514
                  </a>
                  .
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm p-6 md:p-8">
                {/* Honeypot — hidden from people, tempting to bots. The CRM drops
                    any submission that fills it in. */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="company_url">Company website</label>
                  <input
                    type="text"
                    id="company_url"
                    name="company_url"
                    value={formData.company_url}
                    onChange={handleChange}
                    tabIndex={-1}
                    autoComplete="off"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label
                      htmlFor="name"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Name <span className="text-sky-600">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      minLength={2}
                      maxLength={100}
                      value={formData.name}
                      onChange={handleChange}
                      autoComplete="name"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email <span className="text-sky-600">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleChange}
                      autoComplete="email"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      maxLength={40}
                      value={formData.phone}
                      onChange={handleChange}
                      autoComplete="tel"
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="service"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      What can we help with?
                    </label>
                    <select
                      id="service"
                      name="service"
                      value={formData.service}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 cursor-pointer"
                    >
                      <option value="">Select a service</option>
                      {/* Values avoid "/" and "&": the CRM sanitizes on
                          input, so those store as HTML entities and show
                          as &#x2F; / &amp; in the client portal. The
                          visible labels keep the ampersand. */}
                      <option value="Interior Remodeling">Interior Remodeling</option>
                      <option value="Exterior, Siding and Decks">
                        Exterior, Siding &amp; Decks
                      </option>
                      <option value="Handyman and Repairs">Handyman &amp; Repairs</option>
                      <option value="Home Additions and Structural">
                        Home Additions &amp; Structural
                      </option>
                      <option value="Storm Damage or Insurance Claim">
                        Storm Damage / Insurance Claim
                      </option>
                      <option value="Something Else">Something Else</option>
                    </select>
                  </div>
                </div>

                <div className="mt-6">
                  <label
                    htmlFor="message"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Project details
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={5}
                    maxLength={2000}
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us a little about what you need done."
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-sky-500 resize-y"
                  />
                </div>

                {errorMessage && (
                  <p
                    className="mt-6 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3"
                    role="alert"
                  >
                    {errorMessage}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="mt-6 w-full md:w-auto px-8 py-3 bg-sky-600 text-white font-semibold rounded-lg hover:bg-sky-700 focus:outline-none focus:ring-2 focus:ring-sky-500 focus:ring-offset-2 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending…' : 'Request My Free Estimate'}
                </button>

                <p className="mt-4 text-sm text-gray-500">
                  Prefer to talk? Call{' '}
                  <a href="tel:251-348-8514" className="text-sky-600 hover:text-sky-700">
                    251-348-8514
                  </a>
                  .
                </p>
              </form>
            )}
          </section>

          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">
              Contact Information
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <article className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-sky-50 rounded-lg flex-shrink-0">
                  <i className="ri-map-pin-line text-xl text-sky-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    <a
                      href="https://www.google.com/maps/search/S%26L+Contractors+Mobile+AL"
                      target="_blank"
                      rel="noopener noreferrer nofollow"
                      className="no-underline text-inherit hover:text-sky-600 transition-colors cursor-pointer"
                    >
                      Service Area
                    </a>
                  </h4>
                  <address className="not-italic">
                    <p className="text-gray-600">Mobile, AL 36609</p>
                    <p className="text-gray-600">Serving Baldwin &amp; Mobile Counties</p>
                  </address>
                </div>
              </article>

              <article className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-sky-50 rounded-lg flex-shrink-0">
                  <i className="ri-phone-line text-xl text-sky-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Phone</h4>
                  <p className="text-gray-600">
                    <a href="tel:251-348-8514" className="hover:text-sky-600 cursor-pointer">
                      251-348-8514
                    </a>
                  </p>
                  <p className="text-gray-600">
                    <a href="tel:251-454-1737" className="hover:text-sky-600 cursor-pointer">
                      251-454-1737
                    </a>
                  </p>
                </div>
              </article>

              <article className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-sky-50 rounded-lg flex-shrink-0">
                  <i className="ri-mail-line text-xl text-sky-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Email</h4>
                  <p className="text-gray-600">
                    <a
                      href="mailto:SL.Contractors.AL@gmail.com"
                      className="hover:text-sky-600 cursor-pointer"
                    >
                      SL.Contractors.AL@gmail.com
                    </a>
                  </p>
                </div>
              </article>

              <article className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-sky-50 rounded-lg flex-shrink-0">
                  <i className="ri-time-line text-xl text-sky-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Hours of Operation</h4>
                  <p className="text-gray-600">Monday - Saturday</p>
                  <p className="text-gray-600">8:00 AM - 8:00 PM</p>
                </div>
              </article>

              <article className="flex items-start space-x-4">
                <div className="w-12 h-12 flex items-center justify-center bg-sky-50 rounded-lg flex-shrink-0">
                  <i className="ri-facebook-circle-line text-xl text-sky-600"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Follow Us</h4>
                  <a
                    href="https://www.facebook.com/profile.php?id=61551840254912"
                    target="_blank"
                    rel="noopener noreferrer nofollow"
                    className="text-sky-600 hover:text-sky-700 font-medium cursor-pointer"
                  >
                    Facebook Page
                  </a>
                </div>
              </article>
            </div>

            {/* Quote Box */}
            <div className="mt-12 bg-[#ebe5d9] rounded-lg p-8">
              <p className="text-gray-700 italic text-center">
                &quot;And whatever you do, work heartily, as for the Lord and not for men&quot;
              </p>
              <p className="text-gray-500 text-sm text-center mt-2">- Colossians 3:23</p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
